import { Container, Eyebrow, MButton, ArrowRight, MarketingPage, IMG } from "./ui";
import { breadcrumb } from "./seo";

/* ─── Lifecycle timeline ──────────────────────────────────────────────────── */
const LIFECYCLE: { stage: string; title: string; body: string }[] = [
  {
    stage: "Create",
    title: "An interview template, generated for the role",
    body: "Choose technical, non-technical or language. Set role title, seniority, language and duration. We produce a rubric and a structured question plan, and bind it to an AI persona and replica.",
  },
  {
    stage: "Invite",
    title: "One email kicks off scheduling",
    body: "Add a candidate and we send the intro email with a reply-to that routes back into the platform. No portals to log into, no calendar links to manage.",
  },
  {
    stage: "Schedule",
    title: "Email becomes the scheduler",
    body: "The candidate replies in plain English. Our AI reads the thread, proposes or confirms a slot, and the invitation moves to scheduled with a single-use join link valid only inside the window.",
  },
  {
    stage: "Interview",
    title: "A real-time AI video conversation",
    body: "At the appointed time the candidate opens one link. An AI video session spins up — recorded to private storage — and the AI interviewer adapts its follow-ups live.",
  },
  {
    stage: "Evaluate",
    title: "Transcript in, decision-ready report out",
    body: "Transcript, rubric and integrity signals go to our evaluation model. Out comes an overall score, dimension scores, summary, strengths, concerns, a recommendation and the recording.",
  },
  {
    stage: "Bill",
    title: "Pay only for what completed",
    body: "Each completed-and-evaluated interview emits a usage event and is billed at its locked, duration-based price. Delinquent accounts are blocked with a clear state — never a surprise.",
  },
];

function Lifecycle() {
  return (
    <section className="py-24">
      <Container>
        <div className="max-w-2xl">
          <Eyebrow>The lifecycle</Eyebrow>
          <h2 className="mt-5 text-4xl font-bold tracking-tight text-warm-ink sm:text-5xl">
            Six stages. Zero manual handoffs.
          </h2>
        </div>
        <div className="mt-14 grid gap-6 md:grid-cols-2">
          {LIFECYCLE.map((s, i) => (
            <div key={s.stage} className="flex gap-5 rounded-3xl border border-warm-border bg-warm-card p-6">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-warm-ink text-sm font-bold text-warm-bg">
                {i + 1}
              </div>
              <div>
                <div className="text-xs font-semibold uppercase tracking-[0.14em] text-warm-accent">{s.stage}</div>
                <h3 className="mt-1 text-lg font-semibold text-warm-ink">{s.title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed text-warm-muted">{s.body}</p>
              </div>
            </div>
          ))}
        </div>
      </Container>
    </section>
  );
}

/* ─── Roles ───────────────────────────────────────────────────────────────── */
function Roles() {
  const roles: { role: string; can: string[] }[] = [
    {
      role: "For your business",
      can: [
        "Create reusable interview templates",
        "Invite candidates and auto-schedule by email",
        "Review evaluation reports and watch recordings",
        "Manage your team with roles and invites",
      ],
    },
    {
      role: "For candidates",
      can: [
        "Reply to one email to pick a time",
        "Join from the browser — nothing to install",
        "Complete a fair, structured AI interview",
        "A single, time-boxed, private link",
      ],
    },
    {
      role: "For platform admins",
      can: [
        "Configure every provider key in one place",
        "Monitor companies, usage and billing",
        "Tune the cost-plus pricing model",
        "Full operational visibility end to end",
      ],
    },
  ];
  return (
    <section className="bg-warm-sand/40 py-24">
      <Container>
        <div className="max-w-2xl">
          <Eyebrow>Three roles, one platform</Eyebrow>
          <h2 className="mt-5 text-4xl font-bold tracking-tight text-warm-ink sm:text-5xl">
            Built for everyone in the loop.
          </h2>
        </div>
        <div className="mt-14 grid gap-6 lg:grid-cols-3">
          {roles.map((r) => (
            <div key={r.role} className="rounded-3xl border border-warm-border bg-warm-card p-7">
              <h3 className="text-xl font-semibold text-warm-ink">{r.role}</h3>
              <ul className="mt-5 space-y-3">
                {r.can.map((c) => (
                  <li key={c} className="flex gap-3 text-[15px] text-warm-muted">
                    <span className="mt-1 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-warm-accent/15 text-warm-accent">
                      <svg viewBox="0 0 24 24" className="h-2.5 w-2.5" fill="none" stroke="currentColor" strokeWidth="3.5">
                        <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </span>
                    {c}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </Container>
    </section>
  );
}

/* ─── Security ────────────────────────────────────────────────────────────── */
function Security() {
  const items: [string, string][] = [
    ["Encrypted at rest", "Provider keys are AES-GCM encrypted and never returned to non-admins."],
    ["Tenant isolation", "RBAC scopes every query to your company — no cross-tenant reads, ever."],
    ["Signed webhooks", "Stripe, Resend and the video provider are signature-verified and idempotent."],
    ["Private recordings", "Sessions live in private object storage, served only through signed URLs."],
    ["Rotating sessions", "Short-lived access tokens with rotating, httpOnly refresh — revocable on demand."],
    ["Hardened auth", "PBKDF2-SHA256 passwords, single-use reset and invite tokens, session revocation."],
  ];
  return (
    <section className="py-24">
      <Container>
        <div className="max-w-2xl">
          <Eyebrow>Security</Eyebrow>
          <h2 className="mt-5 text-4xl font-bold tracking-tight text-warm-ink sm:text-5xl">
            Enterprise-grade from the first interview.
          </h2>
        </div>
        <div className="mt-14 grid gap-px overflow-hidden rounded-3xl border border-warm-border bg-warm-border sm:grid-cols-2 lg:grid-cols-3">
          {items.map(([t, d]) => (
            <div key={t} className="bg-warm-card p-7">
              <h3 className="font-semibold text-warm-ink">{t}</h3>
              <p className="mt-2 text-[15px] leading-relaxed text-warm-muted">{d}</p>
            </div>
          ))}
        </div>
      </Container>
    </section>
  );
}

export function Platform() {
  return (
    <MarketingPage
      seo={{
        title: "Platform — WonderfulInterview",
        description:
          "One system that schedules, conducts, proctors and scores interviews — from the first email to a decision-ready, integrity-checked evaluation.",
        path: "/platform",
        jsonLd: breadcrumb([
          { name: "Home", path: "/" },
          { name: "Platform", path: "/platform" },
        ]),
      }}
    >
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-96"
          style={{ background: "radial-gradient(60% 60% at 50% 0%, rgba(244,201,155,0.5) 0%, rgba(244,201,155,0) 70%)" }}
        />
        <Container className="relative pt-20 pb-12 text-center sm:pt-28">
          <Eyebrow>The platform</Eyebrow>
          <h1 className="mx-auto mt-7 max-w-3xl text-balance text-5xl font-bold leading-[1.05] tracking-tight sm:text-6xl">
            One system that <span className="font-display italic font-normal text-warm-accent">schedules, conducts, proctors and scores</span>.
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-warm-muted">
            WonderfulInterview replaces the recruiter coordination layer with automation — from the first email to
            a decision-ready evaluation, with integrity evidence attached.
          </p>
          <div className="mt-8 flex justify-center">
            <MButton to="/signup" className="px-6 py-3 text-base">
              Start free <ArrowRight />
            </MButton>
          </div>
        </Container>
        <Container className="relative pb-8">
          <div className="overflow-hidden rounded-4xl border border-warm-border shadow-[0_30px_80px_-30px_rgba(36,31,25,0.35)]">
            <img src={IMG.lake} alt="Mountain lake reflecting warm dawn light" className="h-[280px] w-full object-cover sm:h-[400px]" />
          </div>
        </Container>
      </section>
      <Lifecycle />
      <Roles />
      <Security />
    </MarketingPage>
  );
}
